import { Component, OnInit, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { AppState } from 'src/app/store';
import * as BusinessActions from 'src/app/store/business/business.actions';
import * as BusinessSelectors from 'src/app/store/business/business.selectors';
import * as UserProfileSelectors from 'src/app/store/user-profile/user-profile.selector';
import * as AuthSelectors from 'src/app/store/auth/auth.selectors';
import { BusinessInformationEntity } from 'src/app/models/business.model';
import { AppointmentEntity } from 'src/app/models/appointment.model';
import { AppointmentService } from 'src/app/services/appointment/appointment.service';
import { PostEntity } from 'src/app/models/post.model';
import { PostService } from 'src/app/services/posts/post.service';
import { AngularFireAuth } from '@angular/fire/auth';
import {
  Observable,
  Subscription,
  combineLatest,
  BehaviorSubject,
  of,
} from 'rxjs';
import { map, take, switchMap, catchError } from 'rxjs/operators';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
})
export class HomeComponent implements OnInit, OnDestroy {
  businesses$: Observable<BusinessInformationEntity[]> = this.store.select(
    BusinessSelectors.selectFilteredBusinesses
  );
  isLoading$: Observable<boolean> = this.store.select(
    BusinessSelectors.selectBusinessLoading
  );
  isLoadingMore$: Observable<boolean> = this.store.select(
    BusinessSelectors.selectBusinessLoadingMore
  );
  error$: Observable<string | null> = this.store.select(
    BusinessSelectors.selectBusinessError
  );
  currentZipCode$: Observable<string | null> = this.store.select(
    BusinessSelectors.selectCurrentZipCode
  );
  hasMore$: Observable<boolean> = this.store.select(
    BusinessSelectors.selectHasMoreBusinesses
  );
  lastDocumentId$: Observable<string | null> = this.store.select(
    (state: AppState) => state.business.lastDocumentId
  );

  readonly isAuthenticated$ = this.store.select(
    AuthSelectors.selectIsAuthenticated
  );
  readonly userProfile$ = this.store.select(
    UserProfileSelectors.selectCurrentUserProfile
  );

  appointments$ = new BehaviorSubject<AppointmentEntity[]>([]);
  isLoadingAppointments = false;
  imageLoadingStates: Map<string, boolean> = new Map();
  posts: PostEntity[] = [];
  isLoadingPosts = false;
  postsError: string | null = null;

  private subscriptions = new Subscription();
  private hasRequestedLocation = false;

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private appointmentService: AppointmentService,
    private readonly postService: PostService,
    private readonly afAuth: AngularFireAuth
  ) {}

  ngOnInit(): void {
    // Subscribe to zipcode changes
    this.subscriptions.add(
      this.currentZipCode$.subscribe((zipCode) => {
        if (zipCode) {
          // Zipcode is set, businesses will be loaded by the effect
          this.hasRequestedLocation = true;
        } else if (!this.hasRequestedLocation) {
          // No zipcode and haven't requested location yet, get current location
          this.getCurrentLocation();
          this.hasRequestedLocation = true;
        }
      })
    );

    // Check if businesses are already loaded
    this.subscriptions.add(
      this.businesses$.subscribe((businesses) => {
        if (businesses.length === 0 && !this.hasRequestedLocation) {
          // No businesses loaded and no zipcode, get current location
          this.getCurrentLocation();
          this.hasRequestedLocation = true;
        }
      })
    );

    // Load completed appointments if user is authenticated
    this.subscriptions.add(
      this.isAuthenticated$.subscribe((isAuthenticated) => {
        if (isAuthenticated) {
          this.loadCompletedAppointments();
        }
      })
    );

    // Load posts for blog section
    this.subscriptions.add(
      this.afAuth.idToken.subscribe({
        next: () => this.loadPosts(),
        error: () => this.loadPosts(),
      })
    );
  }

  loadCompletedAppointments(): void {
    this.isLoadingAppointments = true;
    this.appointmentService
      .getUserAppointments()
      .pipe(
        catchError((error) => {
          console.error('Error loading appointments:', error);
          this.isLoadingAppointments = false;
          return of([]);
        })
      )
      .subscribe((appointments) => {
        // Filter for completed appointments (status === 'completed' or isCheckedIn === true)
        // Sort by date descending (most recent first)
        const completedAppointments = appointments
          .filter((apt) => apt.status === 'completed' || apt.isCheckedIn === true)
          .sort((a, b) => {
            const dateA = typeof a.date === 'string' ? new Date(a.date) : a.date;
            const dateB = typeof b.date === 'string' ? new Date(b.date) : b.date;
            return dateB.getTime() - dateA.getTime();
          });
        // Show only first 4
        const appointmentsToShow = completedAppointments.slice(0, 4);
        // Initialize loading states for all appointments
        appointmentsToShow.forEach((apt) => {
          this.imageLoadingStates.set(apt.id, true);
        });
        this.appointments$.next(appointmentsToShow);
        this.isLoadingAppointments = false;
      });
  }

  goToAllAppointments(): void {
    this.router.navigate(['/appointments']);
  }

  goToAppointmentDetail(appointmentId: string): void {
    this.router.navigate(['/appointments', appointmentId]);
  }

  formatAppointmentDate(date: Date | string | null | undefined): string {
    if (!date) {
      return '';
    }

    let appointmentDate: Date;

    if (typeof date === 'string') {
      appointmentDate = new Date(date);
    } else if (date instanceof Date) {
      appointmentDate = date;
    } else {
      // Handle Firestore Timestamp or other date-like objects
      appointmentDate = new Date(date as any);
    }

    // Check if the date is valid
    if (isNaN(appointmentDate.getTime())) {
      return '';
    }

    return appointmentDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getCurrentLocation(): void {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const latitude = position.coords.latitude;
          const longitude = position.coords.longitude;
          this.store.dispatch(
            BusinessActions.loadBusinessesByLocation({ latitude, longitude })
          );
        },
        (error) => {
          console.error('Error getting location:', error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    } else {
      console.error('Geolocation is not supported by this browser.');
    }
  }

  loadMore(): void {
    // Use take(1) to only get current values once and prevent infinite loops
    combineLatest([this.currentZipCode$, this.lastDocumentId$])
      .pipe(take(1))
      .subscribe(([zipCode, lastDocumentId]) => {
        if (zipCode) {
          this.store.dispatch(
            BusinessActions.loadMoreBusinesses({
              zipCode,
              lastDocumentId,
            })
          );
        } else {
          // Load more by location if no zipcode
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                const latitude = position.coords.latitude;
                const longitude = position.coords.longitude;
                this.store.dispatch(
                  BusinessActions.loadMoreBusinessesByLocation({
                    latitude,
                    longitude,
                    lastDocumentId,
                  })
                );
              },
              (error) => {
                console.error('Error getting location for load more:', error);
              }
            );
          }
        }
      });
  }

  goToBusinessDetail(businessId: string): void {
    this.router.navigate(['/businesses', businessId]);
  }

  getStoreTypeIcon(types: string[]): string {
    if (types.includes('nail')) return '💅';
    if (types.includes('spa')) return '🧖';
    if (types.includes('hair')) return '✂️';
    if (types.includes('tattoo')) return '🎨';
    if (types.includes('waxing')) return '🪶';
    if (types.includes('pets')) return '🐾';
    return '🏪';
  }

  onImageLoad(appointmentId: string): void {
    this.imageLoadingStates.set(appointmentId, false);
  }

  onImageError(appointmentId: string): void {
    this.imageLoadingStates.set(appointmentId, false);
  }

  isImageLoading(appointmentId: string): boolean {
    return this.imageLoadingStates.get(appointmentId) !== false;
  }

  setImageLoading(appointmentId: string, loading: boolean): void {
    this.imageLoadingStates.set(appointmentId, loading);
  }

  trackByPostId(_: number, post: PostEntity): string {
    return post.id;
  }

  loadPosts(): void {
    this.isLoadingPosts = true;
    this.postsError = null;
    this.postService.getPosts({ pageSize: 12 }).subscribe({
      next: (response) => {
        this.posts = response?.posts ?? [];
      },
      error: (err) => {
        console.error('Failed to load posts', err);
        this.postsError = 'Unable to load posts right now.';
      },
      complete: () => {
        this.isLoadingPosts = false;
      },
    });
  }

  goToAllBlogs(): void {
    this.router.navigate(['/blog']);
  }
}
